// How a file moves, drawn once.
//
// The three paths that exist: what we ship, what comes back, and what this
// interface does (which is read, and nothing else). Laid out as data rather than
// as hand-written SVG so a step can be added without moving forty coordinates.

interface Stop {
  title: string;
  note?: string;
  /** The bank is the other party — the only box that is not ours. */
  tone?: "bank" | "ours";
}

interface Flow {
  label: string;
  stops: Stop[];
}

const FLOWS: Flow[] = [
  {
    label: "Outbound — a caller raises charges, we ship them",
    stops: [
      { title: "Your service", note: "SigV4, in-account" },
      { title: "Betalingsservice API", note: "builds the delivery" },
      { title: "S3 outbound", note: "outbound/<mailbox>/" },
      { title: "S3 → SFTP", note: "one Lambda per mailbox" },
      { title: "Bank SFTP", note: "one login per subsystem", tone: "bank" },
    ],
  },
  {
    label: "Inbound — the bank answers, we publish one event per file",
    stops: [
      { title: "Bank SFTP", note: "one login per subsystem", tone: "bank" },
      { title: "SFTP poll", note: "one Lambda per mailbox" },
      { title: "S3 inbound", note: "inbound/<mailbox>/" },
      { title: "Processor", note: "mapInboundFile" },
      { title: "EventBridge", note: "one event per file" },
      { title: "Consumers", note: "moneyflow" },
    ],
  },
];

const BOX_W = 132;
const BOX_H = 46;
const GAP = 30;
const ROW_GAP = 100;
const LABEL_OFFSET = 24;
const WIDTH = 6 * BOX_W + 5 * GAP;

export function PipelineDiagram(): JSX.Element {
  const height = FLOWS.length * ROW_GAP;

  return (
    <div className="diagram-scroll">
      <svg
        className="diagram"
        viewBox={`0 0 ${WIDTH} ${height}`}
        width={WIDTH}
        height={height}
        role="img"
        aria-label="How a file moves between a caller, this service, the bank and its consumers."
      >
        <defs>
          <marker id="arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
            <path d="M0,0 L8,4 L0,8 z" className="diagram-arrow-head" />
          </marker>
        </defs>

        {FLOWS.map((flow, row) => {
          const top = row * ROW_GAP + LABEL_OFFSET;
          return (
            <g key={flow.label}>
              <text className="diagram-flow-label" x={0} y={row * ROW_GAP + 12}>
                {flow.label}
              </text>
              {flow.stops.map((stop, index) => {
                const x = index * (BOX_W + GAP);
                return (
                  <g key={stop.title + String(index)}>
                    <rect
                      x={x}
                      y={top}
                      width={BOX_W}
                      height={BOX_H}
                      rx={3}
                      className="diagram-box"
                      data-tone={stop.tone ?? "ours"}
                    />
                    <text className="diagram-title" x={x + BOX_W / 2} y={top + (stop.note ? 20 : 27)}>
                      {stop.title}
                    </text>
                    {stop.note ? (
                      <text className="diagram-note" x={x + BOX_W / 2} y={top + 34}>
                        {stop.note}
                      </text>
                    ) : null}
                    {index < flow.stops.length - 1 ? (
                      <line
                        className="diagram-arrow"
                        x1={x + BOX_W + 4}
                        y1={top + BOX_H / 2}
                        x2={x + BOX_W + GAP - 6}
                        y2={top + BOX_H / 2}
                        markerEnd="url(#arrow)"
                      />
                    ) : null}
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
